import { Button } from "@material-ui/core";
import { DEFAULT_INITIAL_SEARCH_DATA } from "@saleor/config";
import { configurationMenuUrl } from "@saleor/configuration";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import useStateFromProps from "@saleor/hooks/useStateFromProps";
import { commonMessages } from "@saleor/intl";
import MembersErrorDialog from "@saleor/permissionSite/components/MembersErrorDialog";
import {
  usersDiff
} from "@saleor/permissionSite/utils";
import useStaffMemberSearch from "@saleor/searches/useStaffMemberSearch";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createSortHandler from "@saleor/utils/handlers/sortHandler";
import { getSortParams } from "@saleor/utils/sort";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import AssignMembersDialog from "../../components/AssignMembersDialog";
import PermissionSiteDetailsPage, {
  PermissionSiteDetailsPageFormData
} from "../../components/PermissionSiteDetailsPage";
import UnassignMembersDialog from "../../components/UnassignMembersDialog";
import { usePermissionSiteUpdate } from "../../mutations";
import { usePermissionSiteDetailsQuery } from "../../queries";
import { PermissionSiteUpdate } from "../../types/PermissionSiteUpdate";
import {
  permissionSiteDetailsUrl,
  PermissionSiteDetailsUrlDialog,
  PermissionSiteDetailsUrlQueryParams
} from "../../urls";

interface PermissionSiteDetailsProps {
  params: PermissionSiteDetailsUrlQueryParams;
}

export const PermissionSiteDetails: React.FC<PermissionSiteDetailsProps> = ({
  params
}) => {
  const navigate = useNavigator();

  const notify = useNotifier();
  const intl = useIntl();


  const { data, loading, refetch } = usePermissionSiteDetailsQuery({
    displayLoader: true,
    variables: null
  });

  const [membersList, setMembersList] = useStateFromProps(
    data?.permissionSite.users
  );

  const [membersModified, setMembersModified] = useState(false);

  const { search, result: searchResult, loadMore } = useStaffMemberSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });

  const handleUpdateSuccess = (data: PermissionSiteUpdate) => {
    if (data.permissionSiteUpdate.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges)
      });
      refetch();
      closeModal();
    } else if (
      data.permissionSiteUpdate.errors.some(e => e.field === "removeUsers")
    ) {
      openModal("unassignError");
    }
  };

  const { isSelected, listElements, toggle, toggleAll } = useBulkActions(
    params.ids
  );

  const [
    permissionSiteUpdate,
    permissionSiteUpdateResult
  ] = usePermissionSiteUpdate({
    onCompleted: handleUpdateSuccess
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    PermissionSiteDetailsUrlDialog,
    PermissionSiteDetailsUrlQueryParams
  >(navigate, params => permissionSiteDetailsUrl(params), params);

  const handleSort = createSortHandler(
    navigate,
    params => permissionSiteDetailsUrl(params),
    params
  );

  const unassignMembers = () => {
    setMembersList(membersList?.filter(m => !listElements.includes(m.id)));
    setMembersModified(true);
    closeModal();
  };


  const disabled = loading ;

  const handleSubmit = async (formData: PermissionSiteDetailsPageFormData) => {
    const result = await permissionSiteUpdate({
      variables: {
        input: {
          ...usersDiff(data?.permissionSite, formData)
        }
      }
    });

    return result.data.permissionSiteUpdate.errors;
  };

  return (
    <>
      <PermissionSiteDetailsPage
        permissionSite={data?.permissionSite}
        members={membersList || []}
        membersModified={membersModified}
        onAssign={() => openModal("assign")}
        onUnassign={ids => openModal("unassign", { ids })}
        errors={
          permissionSiteUpdateResult?.data?.permissionSiteUpdate.errors || []
        }
        onBack={() => navigate(configurationMenuUrl)}
        onSubmit={handleSubmit}
        saveButtonBarState={permissionSiteUpdateResult.status}
        disabled={disabled}
        toggle={toggle}
        toggleAll={toggleAll}
        isChecked={isSelected}
        selected={listElements.length}
        sort={getSortParams(params)}
        toolbar={
          <Button
            color="primary"
            onClick={() => openModal("unassign", { ids: listElements })}
          >
            {intl.formatMessage({
              defaultMessage: "Unassign",
              description: "button title"
            })}
          </Button>
        }
        onSort={handleSort}
      />
      <AssignMembersDialog
        loading={searchResult.loading}
        staffMembers={searchResult?.data?.search.edges.map(edge => edge.node)}
        onSearchChange={search}
        onFetchMore={loadMore}
        disabled={disabled}
        hasMore={searchResult?.data?.search.pageInfo.hasNextPage}
        initialSearch=""
        confirmButtonState={permissionSiteUpdateResult.status}
        open={params.action === "assign"}
        onClose={closeModal}
        onSubmit={formData => {
          setMembersList([
            ...membersList,
            ...formData.filter(member => !membersList.includes(member))
          ]);
          setMembersModified(true);
          closeModal();
        }}
      />
      <UnassignMembersDialog
        onConfirm={unassignMembers}
        confirmButtonState={permissionSiteUpdateResult.status}
        quantity={listElements.length}
        open={params.action === "unassign"}
        onClose={closeModal}
      />
      <MembersErrorDialog
        onConfirm={closeModal}
        confirmButtonState={permissionSiteUpdateResult.status}
        open={params.action === "unassignError"}
        onClose={closeModal}
      />
    </>
  );
};

export default PermissionSiteDetails;
